import React, { forwardRef, memo, useEffect, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import { IErrMsg } from './types'
import ErrMessageTooltip from '../Errors/ErrMessageTooltip'
import { DescriptionBtnWithPopup } from '../buttons/PromptBtn/DescriptionBtnWithPopup'
import { RequiredMark } from '../ui/RequiredMark/RequiredMark'
import { RiskyParamUnlockBtn } from './components/RiskyParam/RiskyParamUnlockBtn'
import { INT_REGEX } from '../models/Integer/consts'

export interface IntegerInputProps {
    className?: string
    state: any
    touched?: any
    errors?: any
    errorText?: string
    name: string
    label?: string
    changeWarning?: string
    placeholder?: string
    isDisabled?: boolean
    tooltipText?: string
    isColumn?: boolean
    isLabelHidden?: boolean
    type?: string
    isRequired?: boolean
    isFeedbackFixed?: boolean
    errMsg?: IErrMsg
    onEnter?: Function
    onBlur?: Function
    onChange?: Function

    setState(state: any): any
    setTouched?(touched: any): any
}

export const IntegerInput = memo(
    forwardRef<HTMLInputElement, IntegerInputProps>(function IntegerInput(
        {
            setState,
            setTouched,
            className,
            state,
            touched,
            errors,
            errMsg,
            name,
            label,
            changeWarning,
            placeholder,
            isDisabled,
            tooltipText,
            isColumn,
            isLabelHidden,
            type,
            isRequired,
            isFeedbackFixed,
            onEnter,
            onBlur,
            onChange,
        },
        ref
    ) {
        const [isRiskyParam, setIsRiskyParam] = useState(!!changeWarning)
        const [showIntOnlyTooltip, setShowIntOnlyTooltip] = useState(false)
        const target = useRef(null)

        const isInvalid = touched && touched[name] && errors && errors[name]

        const handleUnlockRiskyParam = () => setIsRiskyParam(false)

        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = event.target.value
            if (INT_REGEX.test(newValue)) {
                setShowIntOnlyTooltip(false)
                setTouched &&
                    setTouched((prev: any) => ({ ...prev, [name]: true }))
                setState((prev: any) => ({ ...prev, [name]: newValue }))
                if (typeof onChange === 'function') onChange(newValue)
            } else {
                setShowIntOnlyTooltip(true)
            }
        }

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setShowIntOnlyTooltip(false)
            if (typeof onBlur === 'function') return onBlur(e)
            setTouched && setTouched((prev: any) => ({ ...prev, [name]: true }))
        }

        const handleOnKeyDown = (evt: any) => {
            if (evt.key === 'Enter' && typeof onEnter === 'function') {
                onEnter(evt)
            }
        }

        useEffect(() => {
            if (state?.[name] === '')
                setState((prev: any) => ({ ...prev, [name]: null }))
        }, [state?.[name]]) // eslint-disable-line

        return (
            <Form.Group
                style={{ maxWidth: '600px' }}
                className={`${className} ${
                    isInvalid && !isFeedbackFixed && 'mb-4'
                } d-flex flex-1 mt-1 mb-1 justify-content-between align-items-baseline`}
            >
                {label && !isLabelHidden ? (
                    <Form.Label
                        style={{
                            maxWidth: isColumn ? '30%' : 'auto',
                            opacity: isRiskyParam ? '50%' : '100%',
                        }}
                        className="label-main mt-1 text-capital pe-2"
                    >
                        <div className="label-main-wrapper p-relative">
                            {label}: {isRequired && <RequiredMark />}
                        </div>
                    </Form.Label>
                ) : null}
                <div
                    style={{ width: '70%' }}
                    className={`d-flex ${!isColumn ? 'flex-1' : ''}`}
                >
                    <span className="d-flex align-items-center flex-column w-100 p-relative">
                        {isRiskyParam && <div className="input-overlay" />}
                        <Form.Control
                            className={`input-main ${
                                tooltipText && 'input-padding'
                            }`}
                            type="string"
                            placeholder={placeholder}
                            value={state?.[name] ?? ''}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            onKeyDown={handleOnKeyDown}
                            disabled={!!isDisabled}
                            isInvalid={isInvalid}
                            ref={ref ?? target}
                            name={name}
                            tabIndex={isRiskyParam ? -1 : 0}
                        />
                        <Form.Control.Feedback
                            className="input-feedback"
                            type="invalid"
                        >
                            <FormattedMessage
                                id={errMsg?.message || 'field-validation-error'}
                                values={errMsg?.values}
                                tagName="span"
                            />
                        </Form.Control.Feedback>
                        {tooltipText && (
                            <DescriptionBtnWithPopup
                                type={'default'}
                                header={label ?? ''}
                                description={tooltipText ?? ''}
                                className="mt-1 input-tooltip"
                            />
                        )}
                    </span>
                    {isRiskyParam && (
                        <RiskyParamUnlockBtn
                            onUnlockConfirm={handleUnlockRiskyParam}
                            changeWarning={changeWarning}
                        />
                    )}
                </div>
                <ErrMessageTooltip
                    message="int-only-toast"
                    error={showIntOnlyTooltip}
                    target={ref ?? target}
                />
            </Form.Group>
        )
    })
)
